import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Wraps any authenticated route. If the user's latest acceptance is on a
 * stale legal-document version (TOS or Privacy Policy specifically), they
 * are redirected to /legal/reaccept where they re-sign before continuing.
 *
 * Recruiters are intentionally excluded — their dedicated `RecruiterGate`
 * already handles re-acceptance of the Recruiter Agreement.
 *
 * super_admin and tester_admin bypass entirely. They sign legal as a
 * formality during seed; gating ops on legal re-accept would lock support
 * out during emergencies.
 */
export default function LegalReacceptGate({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!user) return;
    // Admins bypass
    if (user.role === "super_admin" || user.role === "tester_admin") {
      setStatus("ok");
      return;
    }
    // Don't double-gate while already on the re-accept page
    if (loc.pathname.startsWith("/legal/reaccept")) {
      setStatus("ok");
      return;
    }
    http
      .get("/legal/me/status")
      .then(({ data }) => {
        const tosOk = data?.tos?.accepted !== false;
        const privacyOk = data?.privacy?.accepted !== false;
        // Recruiter agreement is gated separately by RecruiterGate.
        setStatus(tosOk && privacyOk ? "ok" : "needs-sign");
      })
      .catch(() => setStatus("ok")); // fail-open on infra blips
  }, [user, loc.pathname]);

  if (status === "checking") {
    return <div className="p-12 text-center text-white/40" data-testid="legal-gate-loading">Loading…</div>;
  }
  if (status === "needs-sign") {
    return <Navigate to="/legal/reaccept" replace />;
  }
  return children;
}
